import { injectable } from 'inversify';
import { Component } from '../../../concepts/objects';
import { resolve } from '../../Container';
import { ExportAdapterLibrary } from './ExportAdapter';
import { ExportDimension, ExportDimensionType, ExportValue } from './ExportDimension';
import { ExportSource, ExportSourceLibrary } from './ExportSource';

export type SerializedExportSource = Pick<ExportSource, 'id'>;
export type SerializedExportDimension = Pick<ExportDimension, 'id' | 'label' | 'params'> & { typeId: string };

export interface SerializedExportConfiguration {
    source?: SerializedExportSource;
    type?: string;
    dimensions?: SerializedExportDimension[];
}

export interface ExportConfiguration {
    source: ExportSource;
    type: string;
    dimensions: ExportDimension[];
}

export const ExportConfiguration = {
    empty: Object.freeze({ source: null, type: null, dimensions: [] }) as ExportConfiguration
};

@injectable()
export class ExportService {

    @resolve(ExportSourceLibrary) private sourceLib!: ExportSourceLibrary;
    @resolve(ExportAdapterLibrary) private adapterLib!: ExportAdapterLibrary;

    public serializeConfig(config: ExportConfiguration) {
        return {
            source: { id: config?.source?.id ?? null },
            type: config.type ?? null,
            dimensions: config?.dimensions?.map(x => ({ id: x.id, typeId: x.type.id, label: x.label, params: x.params })) ?? [],
        };
    }

    public deserializeConfig(config: SerializedExportConfiguration): ExportConfiguration {
        const type = config.type;
        const source = this.resolveSource(config);
        const objects = this.query({ source, type });
        const dimensionPool = this.queryDimensionPool(objects);
        const dimensions = this.activateDimensions(config, dimensionPool);
        return { source, type, dimensions };
    }

    public query({ source, type }: Pick<ExportConfiguration, 'source' | 'type'>) {
        return source?.read().filter(x => x.type === type) ?? [];
    }

    public export({ source, type, dimensions }: ExportConfiguration) {
        const objects = this.query({ source, type });
        const blocks = dimensions.map(d => d.read(objects));
        const rowCount = Math.max(...blocks.flatMap(b => b.map(v => v.length)));

        const table = [] as ExportValue[][];

        // Head
        // for (const c of dimensions) {
        //     table[0].push(ExportValue(c.id, c.label));
        // }

        // Body
        for (let r = 0; r < rowCount; r++) {
            const row = [] as ExportValue[];
            for (const b of blocks) {
                for (const v of b) {
                    row.push(v[r]);
                }
            }
            table.push(row);
        }

        return table;
    }

    private resolveSource(config: SerializedExportConfiguration) {
        const available = this.sourceLib.getAll().flatMap(x => x.get());
        return available.find(x => x.id === config.source?.id);
    }

    private queryDimensionPool(objects: Component[]) {
        return this.adapterLib.getAll().flatMap(x => x.query(objects)).map(x => x);
    }

    private activateDimensions(config: SerializedExportConfiguration, pool: ExportDimensionType[]) {
        const lookup = new Map(pool.map(x => [x.id, x]));
        const activated = [] as ExportDimension[];
        for (const dm of config?.dimensions ?? []) {
            const type = lookup.get(dm.typeId);
            if (!type) continue; // Do something here?
            const inst = { ...(type.createInstance(dm.params)), label: dm.label };
            activated.push(inst);
        }
        return activated;
    }
}
